/*
  MEXESS — product video over the card image (snippets/product-grid-item.liquid).

  A card whose product has a video carries
    <video class="mxz-card__video" data-mxz-video="<mp4 url>" hidden>
  inside .grid-product__image-mask. Nothing is downloaded until that video's
  turn comes. At most one video plays on the page at any time, and each one
  plays once, then fades back to the photograph.

  - Mouse and trackpad: the video plays while the card is hovered. Leaving
    the card stops it; hovering again plays it again.
  - Touch screens: the cards at least 60% on screen take turns, in page
    order (left to right, top to bottom). A video starts once its card has
    stayed in view for DWELL ms, so a quick scroll loads nothing; when it
    ends, the next card in view takes over. A video that has finished does
    not play again on this page view.

  Skipped entirely for visitors who ask for reduced motion or data saving.
  This file is loaded as a module from every card with a video; the browser runs a
  module once per page however many cards include it.
*/

const SELECTOR = 'video[data-mxz-video]';
const DWELL = 600;        // ms a card has to stay in view before its video loads
const GAP = 400;          // ms between one video ending and the next starting
const STALL = 6000;       // ms to wait for a video to start before moving on
const VISIBLE = 0.6;      // share of the card that has to be on screen

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const saveData = navigator.connection && navigator.connection.saveData;

if (!reducedMotion && !saveData) {
  init();
}

function init() {
  const style = document.createElement('style');
  style.textContent = `
    .grid-product .mxz-card__video {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      opacity: 0;
      transition: opacity 0.35s ease;
      pointer-events: none;
    }
    .grid-product .mxz-card__video.is-playing {
      opacity: 1;
    }
  `;
  document.head.appendChild(style);

  const hover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const player = hover ? hoverPlayer() : scrollPlayer();

  const seen = new WeakSet();
  const scan = () => {
    document.querySelectorAll(SELECTOR).forEach((video) => {
      if (seen.has(video)) return;
      seen.add(video);
      video.muted = true;
      video.hidden = false;
      player.add(video);
    });
  };
  scan();

  // Filters and sorting on collection pages replace the grid without a
  // reload; pick up the new cards.
  let pending = 0;
  new MutationObserver(() => {
    cancelAnimationFrame(pending);
    pending = requestAnimationFrame(scan);
  }).observe(document.body, { childList: true, subtree: true });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) player.pause();
    else player.resume();
  });
}

/* One video at a time: start, stop, and the bookkeeping for both players. */
function controller(onEnd) {
  let current = null;
  let stallTimer = 0;

  function start(video) {
    stop();
    current = video;
    if (!video.src) video.src = video.dataset.mxzVideo;
    video.currentTime = 0;

    clearTimeout(stallTimer);
    stallTimer = setTimeout(() => {
      if (current === video && !video.classList.contains('is-playing')) finish(video);
    }, STALL);

    const played = video.play();
    if (played && played.catch) {
      // Autoplay refused (iOS Low Power Mode, for one): treat it as done.
      played.catch(() => {
        if (current === video) finish(video);
      });
    }
  }

  function stop() {
    clearTimeout(stallTimer);
    if (!current) return;
    current.pause();
    current.classList.remove('is-playing');
    current = null;
  }

  function finish(video) {
    if (current !== video) return;
    stop();
    onEnd(video);
  }

  return {
    get current() { return current; },
    start,
    stop,
    watch(video) {
      video.addEventListener('playing', () => {
        if (current === video) video.classList.add('is-playing');
      });
      video.addEventListener('ended', () => finish(video));
    },
  };
}

function hoverPlayer() {
  const ctl = controller(() => {});
  return {
    add(video) {
      ctl.watch(video);
      const card = video.closest('.grid-product__content') || video.parentElement;
      let timer = 0;
      card.addEventListener('mouseenter', () => {
        timer = setTimeout(() => ctl.start(video), 150);
      });
      card.addEventListener('mouseleave', () => {
        clearTimeout(timer);
        if (ctl.current === video) ctl.stop();
      });
    },
    pause: () => ctl.stop(),
    resume: () => {},
  };
}

function scrollPlayer() {
  const videos = [];              // every registered video, in page order
  const inView = new Set();
  const done = new WeakSet();
  let timer = 0;

  const ctl = controller((video) => {
    done.add(video);
    schedule(GAP);
  });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target.__mxzVideo;
      if (entry.intersectionRatio >= VISIBLE) {
        inView.add(video);
      } else {
        inView.delete(video);
        // Scrolled away mid-play: stop it. It has not played through, so it
        // gets another turn if the card comes back.
        if (ctl.current === video) ctl.stop();
      }
    });
    if (!ctl.current) schedule(DWELL);
  }, { threshold: [0, VISIBLE] });

  function next() {
    if (ctl.current) return;
    const video = videos.find((v) => inView.has(v) && !done.has(v) && v.isConnected);
    if (video) ctl.start(video);
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(next, delay);
  }

  return {
    add(video) {
      ctl.watch(video);
      const card = video.closest('.grid-product__image-mask') || video.parentElement;
      card.__mxzVideo = video;
      videos.push(video);
      videos.sort((a, b) =>
        a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
      observer.observe(card);
    },
    pause: () => {
      clearTimeout(timer);
      ctl.stop();
    },
    resume: () => schedule(DWELL),
  };
}
